/**
 * The space tree (§4.1).
 *
 * Rendered as an actual tree because the two constraints only make sense as
 * shape: maximum depth 3, and a client space cannot have children. Both are
 * triggers in the schema rather than checks in this app, so creating a fourth
 * level fails with the database's own message — which is the message worth
 * showing, since it is the one that will still be true when somebody tries it
 * from psql.
 *
 * §4.1 also says the depth budget is fully spent. The level counter beside each
 * root is there so that is visible rather than a thing to rediscover.
 */

import { Link } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { Form } from 'react-aria-components';
import { useCreateSpace, useSpaces } from '../../api/queries.ts';
import type { SpaceKind, SpaceWithOrganization } from '../../api/types.ts';
import { CAPABILITY_OPTIONS } from '../../components/capabilities.ts';
import { Badge } from '../../components/ui/badge.tsx';
import { Button } from '../../components/ui/button.tsx';
import { Dialog, DialogBody, DialogFooter } from '../../components/ui/dialog.tsx';
import { Alert, EmptyState, ErrorState, Loading } from '../../components/ui/feedback.tsx';
import { Select, TextField } from '../../components/ui/field.tsx';
import { Card, CardHeader, PageHeader } from '../../components/ui/layout.tsx';

interface SpaceNode {
  space: SpaceWithOrganization;
  children: SpaceNode[];
  depth: number;
}

/**
 * Builds the forest from a flat list. A space whose parent is not in the list —
 * because the caller cannot reach the parent — is shown at the top level rather
 * than dropped: the API already decided what this person may see, and hiding a
 * reachable space because its parent is invisible would be this app inventing a
 * rule.
 */
function buildForest(spaces: SpaceWithOrganization[]): SpaceNode[] {
  const byId = new Map(spaces.map((space) => [space.id, space]));
  const childrenOf = new Map<string, SpaceWithOrganization[]>();
  const roots: SpaceWithOrganization[] = [];

  for (const space of spaces) {
    if (space.parentSpaceId && byId.has(space.parentSpaceId)) {
      const siblings = childrenOf.get(space.parentSpaceId) ?? [];
      siblings.push(space);
      childrenOf.set(space.parentSpaceId, siblings);
    } else {
      roots.push(space);
    }
  }

  const build = (space: SpaceWithOrganization, depth: number): SpaceNode => ({
    space,
    depth,
    children: (childrenOf.get(space.id) ?? []).map((child) => build(child, depth + 1)),
  });

  return roots.map((root) => build(root, 1));
}

function SpaceRow({ node }: { node: SpaceNode }) {
  return (
    <>
      <li>
        <div
          className="flex flex-wrap items-center gap-2 border-b border-secondary px-4 py-3 hover:bg-secondary"
          style={{ paddingLeft: `${1 + (node.depth - 1) * 1.5}rem` }}
        >
          {node.depth > 1 ? (
            <span aria-hidden="true" className="text-quaternary">
              └
            </span>
          ) : null}
          <Link
            to="/spaces/$spaceId"
            params={{ spaceId: node.space.id }}
            className="font-medium text-primary no-underline hover:underline"
          >
            {node.space.name}
          </Link>
          <Badge tone={node.space.kind === 'client' ? 'brand' : 'outline'}>{node.space.kind}</Badge>
          {node.space.isTemplate ? <Badge tone="gray">template</Badge> : null}
          <span className="text-xs text-tertiary">level {node.depth}</span>
          {node.space.organization ? (
            <Link
              to="/organizations/$organizationId"
              params={{ organizationId: node.space.organization.id }}
              className="ml-auto text-xs text-tertiary no-underline hover:underline"
            >
              {node.space.organization.name}
            </Link>
          ) : (
            // Only an organization's own space carries the link (§3.1); its
            // children do not, and a `client` child saying "internal" here
            // would contradict the kind badge two elements to the left.
            <span className="ml-auto text-xs text-tertiary">no organization</span>
          )}
        </div>
      </li>
      {node.children.map((child) => (
        <SpaceRow key={child.space.id} node={child} />
      ))}
    </>
  );
}

function CreateSpaceForm({
  spaces,
  onClose,
}: {
  spaces: SpaceWithOrganization[];
  onClose: () => void;
}) {
  const create = useCreateSpace();
  const [name, setName] = useState('');
  const [kind, setKind] = useState<SpaceKind>('internal');
  const [parentSpaceId, setParentSpaceId] = useState('');

  // A client space cannot have children, so it cannot be a parent. The trigger
  // enforces this; excluding it here is so the picker does not offer a choice
  // that is guaranteed to fail.
  const parentOptions = [
    { value: '', label: 'None — a root space' },
    ...spaces
      .filter((space) => space.kind !== 'client')
      .map((space) => ({ value: space.id, label: space.name })),
  ];

  return (
    <Form
      onSubmit={(event) => {
        event.preventDefault();
        create.mutate(
          {
            name: name.trim(),
            kind,
            ...(parentSpaceId ? { parentSpaceId } : {}),
          },
          { onSuccess: onClose },
        );
      }}
    >
      <DialogBody>
        <TextField label="Name" value={name} onChange={setName} isRequired autoFocus />
        <Select
          label="Kind"
          options={[
            { value: 'internal' as const, label: 'internal', description: 'Can have children' },
            {
              value: 'client' as const,
              label: 'client',
              description: 'A leaf — a client space cannot have children',
            },
          ]}
          selectedKey={kind}
          onSelectionChange={setKind}
        />
        <Select
          label="Parent space"
          options={parentOptions}
          selectedKey={parentSpaceId}
          onSelectionChange={setParentSpaceId}
          description="Maximum depth is three, and the budget is fully spent by the intended shape: client → project → shared."
        />
        <Alert tone="info">
          A root space is authorized on your role's <span className="font-mono">space.manage</span>{' '}
          alone; a child space is authorized against the parent. Creating an organization is the
          other path to a space, and it needs only <span className="font-mono">org.manage</span>.
        </Alert>
        {create.isError ? <ErrorState error={create.error} /> : null}
      </DialogBody>
      <DialogFooter>
        <Button variant="tertiary" onPress={onClose}>
          Cancel
        </Button>
        <Button
          type="submit"
          variant="primary"
          isPending={create.isPending}
          isDisabled={name.trim() === ''}
        >
          Create space
        </Button>
      </DialogFooter>
    </Form>
  );
}

export function SpaceListPage() {
  const [capability, setCapability] = useState('document.view');
  const spaces = useSpaces(capability);
  const [creating, setCreating] = useState(false);

  const forest = useMemo(() => buildForest(spaces.data?.spaces ?? []), [spaces.data]);

  return (
    <>
      <PageHeader
        title="Spaces & nodes"
        description="The tree everything resolves against. Grants cascade downward only — the resolution walk goes up from a node and never sideways or down, which is what makes a subsidiary unreachable from its parent."
        actions={
          <Button variant="primary" onPress={() => setCreating(true)}>
            Create space
          </Button>
        }
      />

      <Card padded={false}>
        <CardHeader
          title="Spaces you can reach"
          description="Filtered by the set-based resolution query — the same one search will use. Changing the capability changes which spaces qualify."
          actions={
            <Select
              label="Measured against"
              className="w-64"
              options={CAPABILITY_OPTIONS}
              selectedKey={capability}
              onSelectionChange={setCapability}
            />
          }
        />

        {spaces.isPending ? (
          <Loading />
        ) : spaces.isError ? (
          <div className="p-5">
            <ErrorState error={spaces.error} />
          </div>
        ) : forest.length === 0 ? (
          <EmptyState
            title="No spaces resolve to allow for you"
            description={`Nothing grants you ${capability} anywhere, so §4.6 rule 4 answers with denied. That is the correct answer for a person with no grants — it is not an empty database.`}
          />
        ) : (
          <ul className="flex flex-col">
            {forest.map((node) => (
              <SpaceRow key={node.space.id} node={node} />
            ))}
          </ul>
        )}
      </Card>

      <Dialog title="Create a space" isOpen={creating} onOpenChange={setCreating}>
        {(close) => <CreateSpaceForm spaces={spaces.data?.spaces ?? []} onClose={close} />}
      </Dialog>
    </>
  );
}
